import { useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop` });

export default function ShopSetPassword() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const { t } = useI18n();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (pw.length < 8) { setError(t("shop.storefront.min8")); return; }
    setBusy(true);
    try {
      await shopApi.post(`/${slug}/customer/set-password`, { token, password: pw });
      setDone(true);
      setTimeout(() => navigate(`/shop/${slug}/account`), 1400);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d === "password_too_short" ? t("shop.storefront.min8") : t("shop.storefront.invalid_link"));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4" data-testid="sf-setpw">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2"><KeyRound className="h-6 w-6 text-slate-700" /><h1 className="text-lg font-bold text-slate-900">{t("shop.storefront.set_password_title")}</h1></div>
        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center" data-testid="sf-setpw-done">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-sm text-slate-600">{t("shop.storefront.password_set")}</p>
          </div>
        ) : (
          <>
            {error && <div role="alert" data-testid="sf-setpw-error" className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertCircle className="h-4 w-4" />{error}</div>}
            <label className="mb-1 block text-sm text-slate-600">{t("shop.storefront.new_password")}</label>
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="sf-setpw-input"
              className="mb-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            <button onClick={submit} disabled={busy || !token} data-testid="sf-setpw-submit"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 font-semibold text-white disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("shop.storefront.set_password")}
            </button>
            {!token && <p className="mt-3 text-xs text-red-500">{t("shop.storefront.invalid_link")}</p>}
          </>
        )}
        <Link to={`/shop/${slug}`} className="mt-4 block text-center text-xs text-slate-400 hover:text-slate-600">{t("shop.storefront.back")}</Link>
      </div>
    </div>
  );
}
